/**
 * Kanban Columns API Service
 *
 * Servizio per la gestione delle colonne Kanban dinamiche.
 * Supporta operazioni CRUD per colonne e gestione alert scadenze.
 * Simile al Planner di Microsoft Teams.
 */

import axiosInstance from '../../lib/axios-client';
import { buildQueryParams } from './base';
import type {
  KanbanColumnDefinition,
  CreateKanbanColumnRequest,
  UpdateKanbanColumnRequest,
  ReorderKanbanColumnsRequest,
  MoveTaskToDynamicColumnRequest,
  DynamicKanbanBoard,
  DeadlineAlert,
  SnoozeAlertRequest,
  DismissAlertRequest,
  UserAlertPreferences,
  KanbanBoardFilters,
  RequestLookupItem,
  ApiResponse,
} from '../../types/api';

// Re-export types for convenience
export type {
  KanbanColumnDefinition,
  CreateKanbanColumnRequest,
  UpdateKanbanColumnRequest,
  ReorderKanbanColumnsRequest,
  MoveTaskToDynamicColumnRequest,
  DynamicKanbanBoard,
  DeadlineAlert,
  SnoozeAlertRequest,
  DismissAlertRequest,
  UserAlertPreferences,
  KanbanBoardFilters,
  RequestLookupItem,
};

const API_URL = '/api/KanbanColumn';

// ============== Column Management ==============

/**
 * Ottiene tutte le colonne Kanban attive
 */
export const getAllColumns = async (): Promise<KanbanColumnDefinition[]> => {
  const response = await axiosInstance.get<ApiResponse<KanbanColumnDefinition[]>>(API_URL);
  return response.data.data;
};

/**
 * Ottiene una colonna specifica per ID
 */
export const getColumnById = async (id: number): Promise<KanbanColumnDefinition> => {
  const response = await axiosInstance.get<ApiResponse<KanbanColumnDefinition>>(`${API_URL}/${id}`);
  return response.data.data;
};

/**
 * Crea una nuova colonna Kanban
 */
export const createColumn = async (data: CreateKanbanColumnRequest): Promise<KanbanColumnDefinition> => {
  const response = await axiosInstance.post<ApiResponse<KanbanColumnDefinition>>(API_URL, data);
  return response.data.data;
};

/**
 * Aggiorna una colonna Kanban esistente
 */
export const updateColumn = async (
  id: number,
  data: UpdateKanbanColumnRequest
): Promise<KanbanColumnDefinition> => {
  const response = await axiosInstance.put<ApiResponse<KanbanColumnDefinition>>(`${API_URL}/${id}`, data);
  return response.data.data;
};

/**
 * Elimina una colonna Kanban. Se la colonna contiene task, `moveTasksToColumnId`
 * indica la colonna di destinazione a cui spostarli (obbligatoria lato backend in quel caso).
 */
export const deleteColumn = async (id: number, moveTasksToColumnId?: number): Promise<void> => {
  const queryString = moveTasksToColumnId !== undefined
    ? buildQueryParams({ moveTasksToColumnId })
    : '';
  const url = queryString ? `${API_URL}/${id}?${queryString}` : `${API_URL}/${id}`;
  await axiosInstance.delete(url);
};

/**
 * Riordina le colonne Kanban
 */
export const reorderColumns = async (data: ReorderKanbanColumnsRequest): Promise<void> => {
  await axiosInstance.post(`${API_URL}/reorder`, data);
};

/**
 * Inizializza le colonne di default
 */
export const initializeDefaultColumns = async (): Promise<void> => {
  await axiosInstance.post(`${API_URL}/initialize`);
};

// ============== Dynamic Board ==============

/**
 * Ottiene la board Kanban con colonne dinamiche, applicando i filtri richiesta/assegnatari/periodo.
 * Le query vengono costruite manualmente con `buildQueryParams`: `assigneeIds` va ripetuto come
 * chiave multipla (`assigneeIds=1&assigneeIds=2`) perché ASP.NET Core non lega `assigneeIds[]=1`.
 */
export const getDynamicBoard = async (filters: KanbanBoardFilters = {}): Promise<DynamicKanbanBoard> => {
  const params: Record<string, string | number | number[]> = {};
  if (filters.requestId !== undefined) params.requestId = filters.requestId;
  if (filters.assigneeIds && filters.assigneeIds.length > 0) params.assigneeIds = filters.assigneeIds;
  if (filters.dateField) params.dateField = filters.dateField;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;

  const queryString = buildQueryParams(params);
  const url = queryString ? `${API_URL}/board?${queryString}` : `${API_URL}/board`;
  const response = await axiosInstance.get<ApiResponse<DynamicKanbanBoard>>(url);
  return response.data.data;
};

/**
 * Sposta un task in una colonna dinamica
 */
export const moveTaskToColumn = async (
  taskId: number,
  data: MoveTaskToDynamicColumnRequest
): Promise<void> => {
  await axiosInstance.post(`${API_URL}/move-task/${taskId}`, data);
};

/**
 * Ricerca richieste per la Select di collegamento task (RequestLookupSelect).
 * `withTasksOnly` limita ai soli progetti che hanno già almeno un task; `includeId`
 * forza l'inclusione di una richiesta specifica anche se non rientra nel filtro/ricerca
 * (serve a preselezionare la richiesta corrente in modifica).
 */
export const getRequestLookups = async (params: {
  search?: string;
  withTasksOnly?: boolean;
  includeId?: number;
} = {}): Promise<RequestLookupItem[]> => {
  const query: Record<string, string | number | boolean> = {};
  if (params.search) query.search = params.search;
  if (params.withTasksOnly !== undefined) query.withTasksOnly = params.withTasksOnly;
  if (params.includeId !== undefined) query.includeId = params.includeId;

  const queryString = buildQueryParams(query);
  const url = queryString ? `${API_URL}/lookups/requests?${queryString}` : `${API_URL}/lookups/requests`;
  const response = await axiosInstance.get<ApiResponse<RequestLookupItem[]>>(url);
  return response.data.data;
};

// ============== Deadline Alerts ==============

/**
 * Ottiene gli alert di scadenza per l'utente corrente
 */
export const getDeadlineAlerts = async (): Promise<DeadlineAlert[]> => {
  const response = await axiosInstance.get<ApiResponse<DeadlineAlert[]>>(`${API_URL}/alerts`);
  return response.data.data;
};

/**
 * Posticipa un alert
 */
export const snoozeAlert = async (data: SnoozeAlertRequest): Promise<void> => {
  await axiosInstance.post(`${API_URL}/alerts/snooze`, data);
};

/**
 * Dismissare permanentemente un alert
 */
export const dismissAlert = async (data: DismissAlertRequest): Promise<void> => {
  await axiosInstance.post(`${API_URL}/alerts/dismiss`, data);
};

/**
 * Ottiene le preferenze di alert dell'utente
 */
export const getAlertPreferences = async (): Promise<UserAlertPreferences> => {
  const response = await axiosInstance.get<ApiResponse<UserAlertPreferences>>(`${API_URL}/alerts/preferences`);
  return response.data.data;
};

/**
 * Aggiorna le preferenze di alert dell'utente
 */
export const updateAlertPreferences = async (data: UserAlertPreferences): Promise<void> => {
  await axiosInstance.put(`${API_URL}/alerts/preferences`, data);
};

// Export as default object
export const kanbanColumnsService = {
  getAllColumns,
  getColumnById,
  createColumn,
  updateColumn,
  deleteColumn,
  reorderColumns,
  initializeDefaultColumns,
  getDynamicBoard,
  moveTaskToColumn,
  getRequestLookups,
  getDeadlineAlerts,
  snoozeAlert,
  dismissAlert,
  getAlertPreferences,
  updateAlertPreferences,
};

export default kanbanColumnsService;
